// Binary protobuf WebSocket client (aquaphobia.v1). Reconnects with backoff
// and carries the player's identity token, so a reload resumes the same
// empire rather than founding a new one.

import {
  ClientMsg, ServerMsg, Mission, JobKind, FleetOp, ShipClass, ChatChannel, SiegeOp,
  ModuleFamily, SeaObjectKind, TorpedoKind, QuakeInfo, SettlementClock, HeatmapKind, CommissionOp,
  type Hello, type Empire, type Event, type Market, type Heatmap,
  type Notice,
  type Site, type State, type ChatMsg, type ClanInfo, type SiegeInfo,
  type Scoreboard, type SeasonReport, type SeasonSummary, type ReturnBriefing, type WarState, type ProductionState, type BuildingPreview, type TimelapseState, type InboxLedgerState, type DeepPartial,
  type DevBuild, type InboxMsg, type Replay, type ReplayRef, type Wreck,
  type ArcadeQueue, type ArcadeMatchFound, type ArcadePhase, type ArcadeSummary,
  type ArcadeHosting, type ArcadeHostActionKind, type ReplayChunk, type ArcadeInput,
  type MaintenanceInfo, type AchievementUnlock, type MapMarker,
  type MarkerAudience, type MarkerKind, type ReportKind, type QuickRefKind,
  type CasinoAction, type CasinoGame, type CasinoLobby, type CasinoResult, type CasinoTable,
  type PlotOp, type Waypoint, type NameSeamount, type CacheAction,
} from '../gen/proto/aquaphobia';
import { PROTOCOL, wireMpx } from '../world';
import { loadToken, saveToken, worldIdFromUrl } from './token';
import { randomHex } from '../util/random';
import { hiddenDocument } from '../render/motion';
import { LOCALE, connection as S } from '../gen/strings';
import { PING_EVERY_MS, PONG_MISS_MAX, SOCKET_BACKOFF_MAX_MS, SOCKET_BACKOFF_MS } from '../gen/presentation';

// The three close codes the game socket ends this client with. They are
// generated from data/contracts/protocol.toml, the same table
// cmd/aquaphobiad and arcadecoord declare them from (R359). A proto
// mismatch means this build can never talk to that server, so it
// disables reconnect; a refused ticket means "get a new ticket", so it
// never runs the backoff path; a season roll means "ask where you play
// now" (R766), and it takes the same no-backoff path the ticket does.
import {
  CLOSE_MAINTENANCE, CLOSE_PROTO_MISMATCH, CLOSE_SEASON_ROLLED, CLOSE_TICKET_REJECTED,
} from '../gen/balance';

/** Why a connection stopped for good. `protocol` means the two builds
 * disagree and a reload fixes it; `ticket` means the meta server and the
 * game world disagree about this seat, which a reload does not fix. */
export type FatalKind = 'protocol' | 'ticket';

/** How many plain reconnect failures in a row make the client SUSPECT a
 * maintenance window (deployment.md §6.1). A window stops the game
 * instances, so a page that was already open sees a dead socket and
 * nothing else; three failures is past the first two backoff steps and
 * still under two seconds of silence. The caller answers it by probing the
 * gate document, and the backoff keeps running either way, because the
 * suspicion is usually wrong. */
const MAINTENANCE_SUSPECT_RETRIES = 3;

/** How many refused tickets in a row end the attempt. Three is enough to
 * ride out one expired ticket and one lost race for the last seat; a
 * fourth means the meta server and the game server disagree about
 * something a retry cannot mend. */
const MAX_TICKET_REJECTIONS = 3;

/** How many unanswered probes the RTT figure keeps (R1358). A probe goes
 * out every `PING_EVERY_MS`, so a pong more than eight periods late finds
 * no stamp and measures nothing. */
const PROBES_KEPT = 8;

/** The close code the Pong watchdog takes (R2829): a plain close, so the
 * backoff dials again and the player sees the link banner of any lost
 * socket. */
const CLOSE_ABNORMAL = 1006;

/**
 * The page a connection lives on (R2829, ui-map-interface.md §16.12):
 * whether it is hidden, and the return that wakes the connection. A test
 * fakes it.
 */
export interface ConnectionPage {
  /** True while the page is hidden: no Ping goes, so no watchdog runs. */
  hidden(): boolean;
  /** Calls `wake` when the page shows again, and answers the function
   *  that stops the calls. */
  onShow(wake: () => void): () => void;
}

/**
 * The page of the browser: `visibilitychange` to visible and a `pageshow`
 * from the back-forward cache wake the connection. With no DOM (a unit
 * test) the page is never hidden and never wakes.
 */
export function browserPage(): ConnectionPage {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return { hidden: () => false, onShow: () => () => {} };
  }
  return {
    hidden: hiddenDocument,
    onShow(wake) {
      const visible = (): void => { if (!document.hidden) wake(); };
      // A page that left its world reloads on this `pageshow`
      // (`reloadOnReturn` of ui/nav.ts), and its closed connection wakes
      // nothing, so the two never act on one return.
      const shown = (ev: PageTransitionEvent): void => { if (ev.persisted) wake(); };
      document.addEventListener('visibilitychange', visible);
      window.addEventListener('pageshow', shown);
      return () => {
        document.removeEventListener('visibilitychange', visible);
        window.removeEventListener('pageshow', shown);
      };
    },
  };
}

/** This page's own id, sent as `?tab=` on every dial (R1113). The arcade
 * coordinator reads it: a dial of the same tab (a reconnect) replaces the
 * tab's older socket, and a dial of another tab of the account kicks
 * nobody. It lives in memory and not in sessionStorage, because a
 * duplicated tab copies sessionStorage and would carry the same id. A
 * reload is a new id, and its old socket closes as the page unloads. */
const TAB_ID = randomHex(8);

/** Where to dial, and what to say on arrival. The ticket is opaque and
 * optional: the legacy direct mode has none, and a reconnect that a resume
 * token can serve does not need one either. */
export interface ConnectTarget {
  url: string;
  ticket?: string;
}

export interface Handlers {
  onHello(h: Hello): void;
  onState(tick: number, state: State): void;
  onEmpire(e: Empire): void;
  /** `capacity` is the stock the field held when it was last filled; the
   * client draws the fill against it (R475). It is 0 for a site that is
   * not a deposit.
   *
   * `effect` is the owner's hold effect, a catalog wire id of
   * `data/contracts/effects.toml`; 0 = none (R910). EVERY owner frame of a
   * player base states it, because the message is applied whole. */
  onSiteOwner(
    siteId: number, faction: number, amount: number, capacity: number, effect: number,
  ): void;
  onSiteAdded(site: Site): void;
  /** The site is gone from the map for good — the seaquake's stage 2 kills
   * every deposit this way. A dead field is deleted, not dimmed: the client
   * must forget the id, because the next hello does not carry it either. */
  onSiteRemoved(siteId: number): void;
  onEvents(events: Event[]): void;
  /** The whole notice, because `event` names the row in the catalog's
   * event table that decides the cue and the toast style. An untagged
   * notice carries an empty id — a refused order — and empty text with an
   * id set is cue-only. `kind` is the pre-event-system tag and stays for a
   * client that reads nothing else. */
  onNotice(n: Notice): void;
  onChat(line: ChatMsg): void;
  onClan(info: ClanInfo): void;
  onMarket(m: Market): void;
  onSieges(sieges: SiegeInfo[]): void;
  onScoreboard(board: Scoreboard): void;
  onSeasonReport(report: SeasonReport): void;
  /** The season summary (R1518): the card that closes a season, from the
   *  lock of the scores to the roll. Optional, because an arcade match
   *  has its own end card. */
  onSeasonSummary?(summary: SeasonSummary): void;
  /** The return briefing (R1605): the sums of an absence, after the
   *  first Empire frame of a return. Optional, because an arcade match
   *  keeps none. */
  onReturnBriefing?(briefing: ReturnBriefing): void;
  /** WAR-dock data, answering a sendWarQuery (plan §4, R83). */
  onWar(war: WarState): void;
  /** The ECONOMY · PRODUCTION income ring, answering a
   * sendProductionQuery (R1489). */
  onProduction?(p: ProductionState): void;
  /** The levels past the next of one building, answering a
   * sendPreviewQuery (R1839). */
  onBuildingPreview?(p: BuildingPreview): void;
  /** The season timelapse of the running season, answering a
   * sendTimelapseQuery (R1595). The bytes decode with net/timelapse.ts. */
  onTimelapse?(t: TimelapseState): void;
  /** The inbox ledger of the seat, answering a sendInboxLedgerQuery
   * (inbox.md §5.6, R1831): the hour rows, the day rows and the
   * departure that SINCE YOU LEFT and the DIGEST sum (§7, R1851). */
  onInboxLedger?(l: InboxLedgerState): void;
  /** Dev only (R89): where the running server's build stands against the
   * repo's HEAD. Only a `-watch-git` server sends it, and only to admin
   * seats — a real deployment never calls this. */
  onDevBuild(dev: DevBuild): void;
  /** The wrecks one step sank (R801): public, and appended to the hello's list. */
  onWrecks(wrecks: Wreck[]): void;
  /** The durable message list, answering a sendInbox fetch (v12). */
  onInbox(msgs: InboxMsg[]): void;
  /** One schematic engagement replay, answering a fetch-replay (v12). */
  onReplay(replay: Replay): void;
  /** One heatmap field (v13): a dense uint8 intensity per world cell, for
   * the mode this client subscribed to with sendHeatmapMode. The payload is
   * the same bytes for every subscriber — heatmaps are not fog-filtered. */
  onHeatmap(h: Heatmap): void;
  /** One achievement grade the seat just reached (achievements.md §4).
   * The hub evaluates the thresholds itself, so the card is immediate; a
   * grade the service reached that the hub did not announce rides back in
   * the batch answer instead. Optional, because the arcade page draws no
   * unlock card. */
  onAchievementUnlock?(u: AchievementUnlock): void;
  /** Where the next seaquake shock strikes (R803, numbers.md §15). Every
   * commander receives the same frame, on a stage entry and on every shock.
   * Optional, because the arcade page holds no seaquake. */
  onQuake?(q: QuakeInfo): void;
  /** The settlement clock of seaquake stage 3 (R2093): the wall instants
   * the drain and the settlement end at. Optional, because the arcade
   * page holds no seaquake. */
  onSettlement?(c: SettlementClock): void;
  /** The whole list of the map markers this client may see
   * (player-reach.md §2.4, R1292). The list REPLACES the last one.
   * Optional, because an arcade match takes no marker. */
  onMarkers?(markers: MapMarker[]): void;
  /** The connection is permanently down (protocol mismatch) — reconnect is
   * disabled and only a reload can help. Render a full-screen stop. */
  onFatal(reason: string, kind: FatalKind): void;
  /** The game server refused the join ticket (close code 4003). Nothing is
   * scheduled: the caller must fetch a fresh ticket from `POST /api/join`
   * and hand it back through `resume()`. Absent in the legacy direct mode,
   * where a 4003 is fatal because nothing can mint a ticket. */
  onTicketRejected?(): void;
  /** The season rolled and the world handed this client over (close code
   * 4002, R766). The seat this socket sat at was given back, so nothing
   * is scheduled here either: the caller must ask `POST /api/join` where
   * this player plays now — naming no world — and hand the answer back
   * through `resume()`. Absent in the legacy direct mode, where no meta
   * server can answer that question, so the client reconnects to the same
   * world on the ordinary backoff instead. */
  onSeasonRolled?(): void;
  /** The world closed this socket because a maintenance window opened
   * (close code 4004, deployment.md §6.1). Nothing is scheduled: the game
   * instances stop behind the gate, so a retry finds nothing that answers.
   * The caller owns the answer — it probes `/maintenance.json` and covers
   * the screen. Absent in a client that shows no cover, which then falls
   * through to the ordinary backoff. */
  onMaintenance?(): void;
  /** Three plain failures in a row. It is a HINT and not a fact: the
   * caller may probe the gate document to find out whether a window stands.
   * The backoff keeps running under it, because the usual cause is a
   * network that came back. */
  onMaintenanceSuspect?(): void;
  /** The maintenance window the meta server plans (deployment.md §6.1),
   * off the hello and off every change. A `startsAtUnix` of 0 means NO
   * window: the operator cancelled it, and a client that counts down takes
   * the countdown off the screen. Optional, because the arcade page draws
   * no drain line. */
  onMaintenanceInfo?(m: MaintenanceInfo): void;

  // Arcade (arcade.md §9). The lobby and the match share one socket, so
  // these four arms arrive on the connection a core client already uses.
  // They are optional: a core page never receives one, and an arcade page
  // never receives half the arms above. An absent handler drops its arm.
  /** Where this connection stands in a queue. It is also the first frame
   *  the arcade server sends, and the frame it sends again after a match. */
  onArcadeQueue?(q: ArcadeQueue): void;
  /** A match locked and this socket holds a seat in it. The `Hello` that
   *  follows carries the match's own rules. */
  onArcadeMatch?(m: ArcadeMatchFound): void;
  /** A lifecycle transition, or the periodic re-statement of one. */
  onArcadePhase?(p: ArcadePhase): void;
  /** The end card, built for this viewer. */
  onArcadeSummary?(s: ArcadeSummary): void;
  /**
   * One batch of replay records, in the file's own framing (R889).
   *
   * A spectator socket carries nothing else: the coordinator tails the
   * record it is already writing and pushes whole records as bytes. The
   * client hands `records` straight to the uvarint splitter — this
   * class decodes none of it, because the file and the stream hold one
   * format and one reader must serve both.
   */
  onReplayChunk?(c: ReplayChunk): void;
  /** The hosting window of a private game (arcade.md §16.2). */
  onArcadeHosting?(h: ArcadeHosting): void;
  // The casino's three server frames (casino.md). They are optional for
  // the reason the arcade arms are: an arcade page never receives one.
  /** The casino front page: the open tables, the quick queue this empire
   *  stands in, and the operator's CLOSED flag. It arrives on the
   *  `ops.casino.lobby_every_sec` cadence and replaces the whole picture. */
  onCasinoLobby?(l: CasinoLobby): void;
  /** The table this reader sits at or watches, whole, on every change. A
   *  hole card reaches its owner alone, so the frame is built per reader. */
  onCasinoTable?(t: CasinoTable): void;
  /** One settled hand. The wheel lands on `roulette_number` and the reels
   *  stop on `reels`, so the animation is driven from this frame alone. */
  onCasinoResult?(r: CasinoResult): void;
  /** An order this client sent for one fleet (R3265): a move, a course, a
   *  deploy from the pens or a pledge. The game page clears the kept stop
   *  reason of that fleet. Optional: no other page keeps one. */
  onFleetOrder?(fleetId: number): void;
}

/**
 * The core arms of a season world that an arcade server never sends. The
 * handler interface requires each one, so a page that draws no season
 * fills them with these no-ops: the arcade page and the replay page.
 */
export const NO_SEASON_ARMS = {
  onChat() {}, onClan() {}, onMarket() {}, onSieges() {},
  onScoreboard() {}, onSeasonReport() {}, onWar() {}, onDevBuild() {},
  onInbox() {}, onReplay() {}, onHeatmap() {}, onWrecks() {},
} satisfies Partial<Handlers>;

/**
 * The handlers of a spectator socket. It carries replay chunks and nothing
 * else, so every core arm receives nothing and is a no-op rather than a
 * path into a page that draws no season. `arms` names the chunk reader,
 * the stop, and any optional arm the page takes.
 */
export function spectatorHandlers(
  arms: Pick<Handlers, 'onFatal'> & Partial<Handlers>,
): Handlers {
  return {
    ...NO_SEASON_ARMS,
    onHello() {}, onState() {}, onEmpire() {}, onSiteOwner() {},
    onSiteAdded() {}, onSiteRemoved() {}, onEvents() {}, onNotice() {},
    ...arms,
  };
}

/**
 * One casino intent, as one discriminated union (casino.md §5).
 *
 * The six casino messages are six arms of the `ClientMsg` oneof, and
 * `Connection.sendCasino` wraps whichever one the `kind` names. The union
 * lives here, beside the sender, so the panels of `ui/casino/` and the
 * overlay's `UIActions.casino` state one shape and the wire stays in
 * `net/`.
 *
 * - `queue-join` enters the quick queue for one game at one stake tier.
 *   `stakeTier` is an index of `CasinoRules.stakes`, never a credit sum.
 * - `queue-leave` leaves the queue this empire stands in. It carries no
 *   field: an empire stands in at most one queue.
 * - `join-table` takes a free seat at one open table, by the id the lobby
 *   listed.
 * - `leave` gives up the seat. Inside a hand it folds at hold'em, stands
 *   at blackjack and lets the bet ride at roulette; the seat frees at
 *   hand end.
 * - `act` is one act at the table. `amount` carries a bet, a raise or a
 *   top-up in credits; `betKind` and `betNumbers` carry a roulette bet,
 *   where the kind is an index of `data/contracts/casino.toml` and the
 *   numbers are the pockets it covers. The three are optional and
 *   default to nothing, because most acts need none of them.
 * - `spin` is one slot-machine pull. Slots settle at once and need no
 *   table, so the intent carries the tier alone.
 */
export type CasinoIntent =
  | { kind: 'queue-join'; game: CasinoGame; stakeTier: number }
  | { kind: 'queue-leave' }
  | { kind: 'join-table'; tableId: number }
  | { kind: 'leave' }
  | {
    kind: 'act';
    action: CasinoAction;
    amount?: number;
    betKind?: number;
    betNumbers?: number[];
  }
  | { kind: 'spin'; stakeTier: number };

/** One leg of a course in world px, as the plot line keeps it (R2589). */
export interface CourseLeg { x: number; y: number; loop: boolean; passes: number }

/** One leg on the wire: millipixels, the loop flag and the pass count. */
function waypointOf(l: CourseLeg): Waypoint {
  return { xMpx: wireMpx(l.x), yMpx: wireMpx(l.y), loop: l.loop, passes: l.passes };
}

/** The mission of a course: PATROL while it holds a loop part (§7, R2589),
 *  else MOVE. The first leg joins no loop part (R2729), so its flag, which
 *  a plot of an old season can still carry, does not count. */
export function courseMission(legs: readonly { loop: boolean }[]): Mission {
  return legs.some((l, i) => i > 0 && l.loop) ? Mission.MISSION_PATROL : Mission.MISSION_MOVE;
}

export class Connection {
  private ws!: WebSocket;
  private backoffMs = SOCKET_BACKOFF_MS;
  /**
   * When the next reconnect attempt fires, as a `performance.now()` stamp —
   * null while connected, and null once the link is permanently down, since
   * nothing is coming (R108). The overlay counts down from it so a player can
   * see the client is still trying rather than hung.
   */
  retryAt: number | null = null;
  /** Consecutive failed attempts since the socket was last open. */
  retries = 0;
  private pingNonce = 0;
  private pingSent = new Map<number, number>();
  /** Pings in a row with no Pong since the socket opened: the Pong
   *  watchdog (R2829). */
  private misses = 0;
  /** The probe each `PING_EVERY_MS`, cleared when the connection stops (R1358). */
  private readonly probe: ReturnType<typeof setInterval>;
  /** Stops the wake calls of the page; the connection stops them when it stops. */
  private readonly unlisten: () => void;
  /** The reconnect the backoff waits on, or null when none waits. A
   *  `resume` clears it, so one dial follows (R1358). */
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  rttMs = 0;
  bytesIn = 0;
  msgsIn = 0;
  connected = false;

  /** Where this connection dials, and the ticket it presents. */
  private target: ConnectTarget;
  /**
   * The world the dialled URL names — the resume token's storage key.
   *
   * Readable from outside because a log out has to forget the token for the
   * world this connection is seated in, and the dialled URL is the only
   * place that world id comes from. Only this class writes it.
   */
  worldId: number;
  /** Refused tickets since the last hello. Three in a row give up. */
  private ticketRejections = 0;

  /**
   * The address this connection dials, with no ticket: SEA VIEW HERE stores
   * it for the sea view, whose page on the game origin cannot find the
   * world socket otherwise (R2494, seaview.md §3.3).
   */
  get url(): string {
    return this.target.url;
  }

  constructor(target: string | ConnectTarget, private handlers: Handlers,
    private readonly page: ConnectionPage = browserPage()) {
    this.target = typeof target === 'string' ? { url: target } : target;
    this.worldId = worldIdFromUrl(this.target.url);
    this.connect();
    this.probe = setInterval(() => this.ping(), PING_EVERY_MS);
    this.unlisten = page.onShow(() => this.wake());
  }

  /**
   * Sends one latency Ping, and runs the Pong watchdog (R2829). A hidden
   * page sends no Ping (R304), so it runs no watchdog: the figure is read
   * from a line nobody looks at, and the browser throttles the timer under
   * it. The interval stays up, so the Ping starts again on the first tick
   * after the return. When the last `PONG_MISS_MAX` Pings got no Pong, the
   * socket is half dead: a telephone may freeze the socket of a hidden page
   * and give no close event. The socket then closes itself, and the backoff
   * dials again.
   */
  private ping(): void {
    if (this.page.hidden() || !this.connected) return;
    if (this.misses >= PONG_MISS_MAX) {
      this.drop();
      return;
    }
    this.misses++;
    const nonce = ++this.pingNonce;
    // A server that sends no pong must not grow the map: a pong older
    // than PROBES_KEPT probes measures nothing (R1358).
    this.pingSent.delete(nonce - PROBES_KEPT);
    this.pingSent.set(nonce, performance.now());
    this.send({ ping: { nonce } });
  }

  /**
   * The page shows again (`visibilitychange` to visible, or a `pageshow`
   * from the back-forward cache, R2829). A socket that waits on the backoff
   * dials at once with the backoff reset: a hidden page may have waited
   * minutes, and the player looks now. An open socket sends a Ping at once,
   * and the watchdog counts from zero. A connection that waits on its
   * caller (a season roll, a maintenance close, a refused ticket) or that
   * stopped for good does nothing.
   */
  wake(): void {
    if (this.fatalReason !== null) return;
    this.misses = 0;
    if (this.connected) {
      this.ping();
      return;
    }
    if (this.retryTimer === null) return;
    this.clearRetry();
    this.backoffMs = SOCKET_BACKOFF_MS;
    this.retryAt = null;
    this.connect();
  }

  /** Drops a socket that answers nothing, and takes the path of a plain
   *  close (R2829). */
  private drop(): void {
    const ws = this.ws;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null; // the late close of the dead socket must not act
    try { ws.close(); } catch { /* already dead */ }
    this.closed(CLOSE_ABNORMAL);
  }

  /** Set once, never cleared: the reason this connection stopped for good. */
  private fatalReason: string | null = null;

  /**
   * Tears the socket down for good under `reason`: no reconnect follows,
   * and a later `connect` or `resume` finds the reason set and does
   * nothing. Answers false when the connection had stopped already.
   */
  private stop(reason: string): boolean {
    if (this.fatalReason !== null) return false;
    this.fatalReason = reason;
    this.retryAt = null; // nothing is coming: the banner must not count down
    this.connected = false;
    clearInterval(this.probe);
    this.unlisten();
    this.clearRetry();
    this.ws.onclose = null; // the pending close must not schedule a reconnect
    try { this.ws.close(); } catch { /* already dead */ }
    return true;
  }

  /** Permanently stops the connection: tears the socket down, disables
   * reconnect and tells the app why. Both refusal paths land here — the
   * server's 4001 close and a hello that carries the wrong protocol
   * (an old server that ignores ?proto= entirely).
   *
   * `kind` travels with the reason because the two stops need different
   * words: a version mismatch is fixed by a reload, and a refused ticket
   * is not fixed by anything the page can do on its own (R158). */
  fatal(reason: string, kind: FatalKind = 'protocol') {
    if (this.stop(reason)) this.handlers.onFatal(reason, kind);
  }

  /**
   * Closes the connection for good and says nothing (R1203): the page is
   * leaving the world on the player's own order, so there is no stop
   * screen to raise and no reconnect to schedule.
   */
  close() {
    this.stop('closed');
  }

  /**
   * Dials a new target: a fresh ticket after a 4003, or another world after
   * the player switched. The backoff starts over, because this is a new
   * attempt and not a retry of the one that failed.
   */
  resume(target: ConnectTarget) {
    if (this.fatalReason !== null) return;
    this.target = target;
    this.worldId = worldIdFromUrl(target.url);
    this.backoffMs = SOCKET_BACKOFF_MS;
    this.retryAt = null;
    this.clearRetry();
    this.connect();
  }

  private clearRetry() {
    if (this.retryTimer === null) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private connect() {
    if (this.fatalReason !== null) return;
    this.misses = 0;
    const token = loadToken(this.worldId);
    const base = this.target.url;
    // ?proto= on every connect (R69): the server refuses a mismatch with
    // close code 4001 instead of letting a stale build play garbled.
    let url = `${base}${base.includes('?') ? '&' : '?'}proto=${PROTOCOL}`;
    // The language this page reads (i18n.md §1.2, R857). A browser's
    // WebSocket cannot set a header, so the tag rides the URL; the
    // server writes it on the connection and renders every notice, toast
    // and catalog row it sends with it.
    url += `&lang=${encodeURIComponent(LOCALE)}`;
    url += `&tab=${TAB_ID}`;
    // The resume token comes first on the server side: it needs no meta
    // round-trip. The ticket rides along for the connection that has no
    // seat to resume yet, and for the one whose token the server refuses.
    if (token) url += `&token=${encodeURIComponent(token)}`;
    if (this.target.ticket) url += `&ticket=${encodeURIComponent(this.target.ticket)}`;
    this.ws = new WebSocket(url);
    this.ws.binaryType = 'arraybuffer';
    this.ws.onopen = () => {
      this.connected = true;
      this.misses = 0;
      this.backoffMs = SOCKET_BACKOFF_MS;
      this.retryAt = null;
      this.retries = 0;
    };
    this.ws.onclose = (ev) => this.closed(ev.code);
    this.ws.onmessage = (ev) => {
      const bytes = new Uint8Array(ev.data as ArrayBuffer);
      this.bytesIn += bytes.length;
      this.msgsIn++;
      let msg: ServerMsg;
      try {
        msg = ServerMsg.decode(bytes);
      } catch (err) {
        // One malformed frame must not wedge the client: drop the socket
        // and let the reconnect backoff bring back a clean stream.
        console.error('undecodable server frame — reconnecting', err);
        this.ws.close();
        return;
      }
      if (msg.hello) {
        // A hello is the only proof the join worked, so it is what clears
        // the refusal count. The token is stored under the world this
        // connection dialled, which is the key the next connect reads.
        this.ticketRejections = 0;
        // The ticket is single-use in spirit: once a seat answers, the
        // resume token carries the reconnect and a stale ticket must not
        // travel again.
        this.target = { url: this.target.url };
        if (msg.hello.token) saveToken(this.worldId, msg.hello.token);
        this.handlers.onHello(msg.hello);
      } else if (msg.state) {
        this.handlers.onState(msg.state.tick, msg.state);
      } else if (msg.empire) this.handlers.onEmpire(msg.empire);
      else if (msg.siteOwner) {
        this.handlers.onSiteOwner(msg.siteOwner.siteId, msg.siteOwner.faction,
          msg.siteOwner.amount, msg.siteOwner.capacity, msg.siteOwner.effect);
      } else if (msg.siteAdded?.site) this.handlers.onSiteAdded(msg.siteAdded.site);
      else if (msg.siteRemoved) this.handlers.onSiteRemoved(msg.siteRemoved.siteId);
      else if (msg.events) this.handlers.onEvents(msg.events.events);
      else if (msg.notice) this.handlers.onNotice(msg.notice);
      else if (msg.chat) this.handlers.onChat(msg.chat);
      else if (msg.clan) this.handlers.onClan(msg.clan);
      else if (msg.market) this.handlers.onMarket(msg.market);
      else if (msg.sieges) this.handlers.onSieges(msg.sieges.sieges);
      else if (msg.scoreboard) this.handlers.onScoreboard(msg.scoreboard);
      else if (msg.seasonReport) this.handlers.onSeasonReport(msg.seasonReport);
      else if (msg.seasonSummary) this.handlers.onSeasonSummary?.(msg.seasonSummary);
      else if (msg.returnBriefing) this.handlers.onReturnBriefing?.(msg.returnBriefing);
      else if (msg.war) this.handlers.onWar(msg.war);
      else if (msg.production) this.handlers.onProduction?.(msg.production);
      else if (msg.buildingPreview) this.handlers.onBuildingPreview?.(msg.buildingPreview);
      else if (msg.timelapse) this.handlers.onTimelapse?.(msg.timelapse);
      else if (msg.inboxLedger) this.handlers.onInboxLedger?.(msg.inboxLedger);
      else if (msg.inbox) this.handlers.onInbox(msg.inbox.msgs);
      else if (msg.replay) this.handlers.onReplay(msg.replay);
      else if (msg.heatmap) this.handlers.onHeatmap(msg.heatmap);
      else if (msg.achievementUnlock) this.handlers.onAchievementUnlock?.(msg.achievementUnlock);
      else if (msg.quake) this.handlers.onQuake?.(msg.quake);
      else if (msg.settlement) this.handlers.onSettlement?.(msg.settlement);
      else if (msg.maintenanceInfo) this.handlers.onMaintenanceInfo?.(msg.maintenanceInfo);
      else if (msg.devBuild) this.handlers.onDevBuild(msg.devBuild);
      else if (msg.wrecks) this.handlers.onWrecks(msg.wrecks.wrecks);
      else if (msg.markers) this.handlers.onMarkers?.(msg.markers.markers);
      else if (msg.arcadeQueue) this.handlers.onArcadeQueue?.(msg.arcadeQueue);
      else if (msg.arcadeMatch) this.handlers.onArcadeMatch?.(msg.arcadeMatch);
      else if (msg.arcadePhase) this.handlers.onArcadePhase?.(msg.arcadePhase);
      else if (msg.arcadeSummary) this.handlers.onArcadeSummary?.(msg.arcadeSummary);
      else if (msg.replayChunk) this.handlers.onReplayChunk?.(msg.replayChunk);
      else if (msg.arcadeHosting) this.handlers.onArcadeHosting?.(msg.arcadeHosting);
      else if (msg.casinoLobby) this.handlers.onCasinoLobby?.(msg.casinoLobby);
      else if (msg.casinoTable) this.handlers.onCasinoTable?.(msg.casinoTable);
      else if (msg.casinoResult) this.handlers.onCasinoResult?.(msg.casinoResult);
      else if (msg.pong) {
        this.misses = 0;
        const sent = this.pingSent.get(msg.pong.nonce);
        if (sent !== undefined) {
          this.rttMs = performance.now() - sent;
          this.pingSent.delete(msg.pong.nonce);
        }
      }
    };
  }

  /** One close of the socket, by its code: the close event, or the Pong
   *  watchdog with a plain close (R2829). */
  private closed(code: number) {
    this.connected = false;
    if (code === CLOSE_PROTO_MISMATCH) {
      this.fatal(S.protocol_refused({ proto: PROTOCOL }));
      return;
    }
    if (code === CLOSE_SEASON_ROLLED && this.handlers.onSeasonRolled) {
      // The world green-fielded its human seats, so this socket's seat
      // may already belong to somebody else (R762, R766). Reconnecting
      // to the same world would be the wrong question, and the right
      // one — where does this player play now — only the caller can
      // ask. No backoff and no timer: the caller owns the next attempt,
      // exactly as it does for a refused ticket.
      //
      // The refusal count is untouched. It counts refused tickets, and
      // a handover is not one; the ticket the caller comes back with
      // gets the whole ceiling of three if the world refuses it.
      this.retryAt = null;
      this.handlers.onSeasonRolled();
      return;
    }
    // A direct-mode client falls through to the backoff path below, and
    // that is the whole of its answer: it dials the same world again and
    // its resume token seats it. Such a client is never handed over in
    // practice — a world with no meta server behind it reads no season
    // plan and green-fields no seat — but a dead socket with no way back
    // is not an acceptable answer to a code it does not know.
    if (code === CLOSE_MAINTENANCE && this.handlers.onMaintenance) {
      // The window opened and every world closed (deployment.md §6.1).
      // No backoff and no fatal: the caller covers the screen and polls
      // the gate document, and the page reloads itself when the gate
      // comes down. `retryAt` is cleared so no banner counts down to an
      // attempt nothing is making.
      this.retryAt = null;
      this.handlers.onMaintenance();
      return;
    }
    if (code === CLOSE_TICKET_REJECTED) {
      this.ticketRejections++;
      if (!this.handlers.onTicketRejected || this.ticketRejections >= MAX_TICKET_REJECTIONS) {
        this.fatal(S.ticket_refused, 'ticket');
        return;
      }
      // No backoff and no timer: the caller owns the next attempt,
      // because only it can ask the meta server for a fresh ticket.
      this.retryAt = null;
      this.handlers.onTicketRejected();
      return;
    }
    this.retries++;
    this.retryAt = performance.now() + this.backoffMs;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, this.backoffMs);
    this.backoffMs = Math.min(this.backoffMs * 2, SOCKET_BACKOFF_MAX_MS);
    // A host behind the gate answers nothing at all, so a page that was
    // open when the window began never reads close code 4004: the
    // process is already down. The third failure in a row is the cue to
    // ask the origin whether a window stands. The backoff above is
    // already scheduled, so a wrong suspicion costs one HTTP request.
    // Every failure past the third asks again, because the backoff caps
    // at ten seconds and a window that opens during a long outage must
    // still be found.
    if (this.retries >= MAINTENANCE_SUSPECT_RETRIES) {
      this.handlers.onMaintenanceSuspect?.();
    }
  }

  /** Encodes one intent. `fromPartial` fills every field a caller leaves
   *  out with the proto3 default, and a default is not written to the
   *  wire, so a sender passes what it means and spells no zero. */
  private send(partial: DeepPartial<ClientMsg>) {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(ClientMsg.encode(ClientMsg.fromPartial(partial)).finish());
      // A new order of a fleet ends the reason it stopped (R3265). HOLD
      // FAST ends the run home of a withdrawing fleet, so it ends the
      // reason of the turn too (R3261).
      const holdFast = partial.fleetAction?.op === FleetOp.FLEET_OP_HOLD_FAST ? partial.fleetAction.fleetId ?? 0 : 0;
      const fleet = partial.order?.fleetId || partial.createFleet?.fleetId || partial.siege?.fleetId || holdFast;
      if (fleet) this.handlers.onFleetOrder?.(fleet);
    }
  }

  /** Whether an intent sent now reaches the world. `send` drops an intent
   *  while the socket is not open and tells nobody, so a press that must
   *  reach the world asks first and tells the player (audit P2, R1851). */
  isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  sendOrder(fleetId: number, xPx: number, yPx: number, mission: Mission) {
    this.send({ order: {
      fleetId, mission, xMpx: wireMpx(xPx), yMpx: wireMpx(yPx),
    } });
  }

  /**
   * The STEER order (numbers.md §4.2, R2638): the wanted heading in
   * milliradians and the step of the engine telegraph. The hub reads no
   * point of it. The helm keys of the map send it (R2707).
   */
  sendSteer(fleetId: number, headingMrad: number, throttle: number) {
    this.send({ order: {
      fleetId, mission: Mission.MISSION_STEER, xMpx: 0, yMpx: 0, headingMrad, throttle,
    } });
  }

  /**
   * A course (v2-the-deep.md §7, R2576, R2589): the commander's legs in
   * world px with the loop flag and the pass count of each, at most
   * `route_legs_max`, with no leg 0. The hub writes the position of the
   * fleet as leg 0 and reads no point of the order. The mission is PATROL
   * while the course holds a loop part, because a loop course is the
   * patrol of v2, and MOVE otherwise; the order line takes its ink. The
   * point of the order is the last leg, for a reader that knows no legs.
   */
  sendCourse(fleetId: number, legs: readonly CourseLeg[]) {
    const last = legs[legs.length - 1];
    if (!last) return;
    this.send({ order: {
      fleetId, mission: courseMission(legs), xMpx: wireMpx(last.x), yMpx: wireMpx(last.y),
      legs: legs.map(waypointOf),
    } });
  }

  /** One intent on the course plots of the empire (R2576): SAVE takes a
   *  name and the legs, RENAME a plot id and a name, DELETE a plot id,
   *  and LANE a plot id and the state of the switch (R2646). */
  sendPlot(op: PlotOp, a: { plotId?: number; name?: string; legs?: readonly CourseLeg[]; lane?: boolean }) {
    this.send({ plot: {
      op, plotId: a.plotId, name: a.name, legs: a.legs?.map(waypointOf), lane: a.lane,
    } });
  }

  /** ASSIGN of a commission (v2-the-deep.md §10, R2634, R2645): one own
   *  fleet to one open offer of the board of the faction. */
  sendCommission(id: number, fleetId: number) {
    this.send({ commission: { op: CommissionOp.COMMISSION_OP_ASSIGN, id, fleetId } });
  }

  /** One cache intent (v2-the-deep.md §9.3, §9.4, R2643, R2649): DEPOSIT
   *  a player cache from an own fleet at its position, or the NOTE of an
   *  own cache. The hub answers a refusal with an error notice, and the
   *  Empire frame carries the cache. */
  sendCache(a: CacheAction) {
    this.send({ cache: a });
  }

  sendViewport(x0: number, y0: number, x1: number, y1: number) {
    this.send({ view: { x0Mpx: wireMpx(x0), y0Mpx: wireMpx(y0), x1Mpx: wireMpx(x1), y1Mpx: wireMpx(y1) } });
  }

  sendEnqueue(kind: JobKind, target: number, count = 0) {
    this.send({ enqueue: { kind, target, count } });
  }

  sendJobAction(jobId: number, cancel: boolean, move = 0) {
    this.send({ job: { jobId, cancel, move } });
  }

  /** Puts a hull that is already in the pens to sea. fleetId names it; the
   * pens hold real fleet objects, so this is a deploy and not a build.
   * target, when given, is baked into the order so the fleet sails off
   * immediately (GO…) instead of idling at the home base. The intent
   * carries no load mix: the hull loads the mix it keeps from the LOAD
   * sheet, or AUTO (R2017). */
  sendCreateFleet(cls: ShipClass, fleetId = 0, captainId = 0, design = 0,
    target?: { xPx: number; yPx: number; mission: Mission }) {
    this.send({ createFleet: {
      cls, fleetId, captainId, design,
      xMpx: target ? wireMpx(target.xPx) : 0,
      yMpx: target ? wireMpx(target.yPx) : 0,
      mission: target?.mission ?? Mission.MISSION_UNSPECIFIED,
    } });
  }

  /** One fleet order. `load` is the load mix of `FLEET_OP_SET_LOAD`, one
   *  row for each kind, and an empty list is AUTO (R2017). */
  sendFleetAction(fleetId: number, op: FleetOp, arg = 0,
    load: readonly { kind: number; count: number }[] = []) {
    this.send({ fleetAction: { fleetId, op, arg, load: load.map((r) => ({ kind: r.kind, count: r.count })) } });
  }

  /** The stow of a Sapper in the pens (R2654): `FLEET_OP_SET_STOW` with
   *  one row for each kind, and an empty list is AUTO. */
  sendStow(fleetId: number, stow: readonly { kind: number; count: number }[]) {
    this.send({ fleetAction: {
      fleetId, op: FleetOp.FLEET_OP_SET_STOW, stow: stow.map((r) => ({ kind: r.kind, count: r.count })),
    } });
  }

  sendCaptainAction(captainId: number, opts: {
    recruit?: boolean; dismiss?: boolean; assignFleet?: number; ransom?: boolean; release?: boolean;
    ownerId?: number;
  }) {
    this.send({ captain: { captainId, ...opts } });
  }

  sendChat(channel: ChatChannel, text: string) {
    this.send({ chat: { channel, text } });
  }

  /**
   * One quick line (R1607): its number and what it names, and no text. The
   * hub resolves the position and refuses a name the sender may not give
   * (R1608).
   */
  sendQuick(channel: ChatChannel, quick: number, kind: QuickRefKind, id: number) {
    this.send({ chat: { channel, quick, quickRef: { kind, id } } });
  }

  /** One aimed round. The point is the shot — the server does not lead.
   * kind picks the base round for fleetId 0 (R86); 0 = the Heavy, and the
   * server ignores it for fleet shots. */
  sendFire(fleetId: number, xPx: number, yPx: number, lockFleetId = 0,
    kind: TorpedoKind = TorpedoKind.TORPEDO_KIND_UNSPECIFIED) {
    this.send({ fire: {
      fleetId, lockFleetId, kind, xMpx: wireMpx(xPx), yMpx: wireMpx(yPx),
    } });
  }

  sendSiegeAction(op: SiegeOp, siteId: number, fleetId: number) {
    this.send({ siege: { op, siteId, fleetId } });
  }

  /** I'M IN on the next window of one object, or its withdrawal (R3279).
   *  The server keys the window; the Sieges frame answers. */
  sendWindowCommit(siteId: number, withdraw: boolean) {
    this.send({ windowCommit: { siteId, withdraw } });
  }

  /** Ask for the WAR dock's data (series, ledgers, log, intel). Sent when
   * the tab opens or a flip lands while it is open — never on a timer. */
  sendWarQuery() {
    this.send({ warQuery: {} });
  }

  /** Ask for the empire's income ring (R1489). Sent when the ECONOMY ·
   * PRODUCTION pill opens, never on a timer. */
  sendProductionQuery() {
    this.send({ productionQuery: {} });
  }

  /** Ask for the levels past the next of one building (R1839). Sent when
   * a building card opens, and again when the Empire frame changes that
   * building while the card stands; never on a timer. */
  sendPreviewQuery(building: number) {
    this.send({ previewQuery: { building } });
  }

  /** Ask for the season timelapse of the running season (R1595). Sent
   * when a view opens the player, never on a timer. */
  sendTimelapseQuery() {
    this.send({ timelapseQuery: {} });
  }

  /** Ask for the inbox ledger of the seat (inbox.md §5.6, R1831). Sent
   * when the INBOX opens, when SINCE YOU LEFT opens or changes its range,
   * when ALL FIGURES opens and on a return (§7, R1851); never on a timer.
   * The world answers one ask a second and makes a later ask wait. */
  sendInboxLedgerQuery() {
    this.send({ inboxLedgerQuery: {} });
  }

  /** Ask the server to state the world again (R778). It answers with a
   * fresh Hello and marks the next State frame full, so a view that holds
   * no picture may build one without a new socket. The arcade page sends
   * it when the player steps back into a running match (R777). */
  sendResync() {
    this.send({ resync: {} });
  }

  /** Subscribe to one heatmap mode, or stop with HEATMAP_KIND_NONE (v13).
   * The subscription belongs to the socket, not to the account: a reconnect
   * starts at NONE, so the client must send this again after a hello. */
  sendHeatmapMode(kind: HeatmapKind) {
    this.send({ heatmapMode: { kind } });
  }

  /** Places one map marker on a cell for an audience (player-reach.md §2.3). */
  sendMarkerPlace(kind: MarkerKind, cell: number, audience: MarkerAudience) {
    this.send({ markerAction: { kind, cell, audience } });
  }

  /** Removes one of this commander's own map markers (player-reach.md §2.7). */
  sendMarkerRemove(id: number) {
    this.send({ markerAction: { removeId: id } });
  }

  sendInvent(hull: ShipClass, modules: { family: ModuleFamily; tier: number }[], weapType = 0, shieldType = 0) {
    this.send({ invent: { hull, weapType, shieldType, modules: modules.map((m) => ({ ...m, effectPct: 0, name: '' })) } });
  }

  sendAdmin(op: string, value = 0) {
    this.send({ admin: { op, value } });
  }

  sendMarket(a: {
    listDesign?: number; price?: number; buy?: number; cancel?: number; listIsotopes?: number;
  }) {
    this.send({ market: a });
  }

  /** The inbox's intents (v12; v17 for the replay, R526): the list and its
   * replays travel on demand only — the unread badge already rides the
   * Empire frame. A replay is asked for with the reference the message
   * carried, which the client treats as opaque and sends back unchanged. */
  sendInbox(a: { fetch?: boolean; markRead?: number; markAll?: boolean; markIds?: number[]; fetchReplay?: ReplayRef }) {
    this.send({ inbox: a });
  }

  /** REPLACE (fleet-atom-redesign.md §11, R1308): press the Replace action
   * of one loss message. The server queues the build or refuses it with a
   * notice, and answers with the Inbox frame either way. */
  sendInboxReplace(msgId: number) {
    this.send({ inboxReplace: { msgId } });
  }

  /** Flip one of the empire's own settings, by key. The server owns the
   * value: it answers with the next Empire frame, and refuses a key it does
   * not know with a notice. */
  sendEmpireSetting(key: string, on: boolean) {
    this.send({ empireSetting: { key, on } });
  }

  /** Put one own fleet on one control group (R539). The server is the store:
   * it refuses a slot outside `1..ControlGroupSlots` and a fleet the player
   * does not own, and it states every taken slot in the next Empire frame. */
  sendControlGroup(slot: number, fleetId: number) {
    this.send({ controlGroup: { slot, fleetId } });
  }

  /** Say that one control-group digit recalled a group (R539).
   *
   * The recall itself is the client's work — the selection and the camera
   * both happen on the frame of the press — and the server changes nothing
   * on this intent. It counts the press for an achievement and no more
   * (achievements.md §3). An empty slot sends nothing at all. */
  sendControlGroupRecall(slot: number) {
    this.send({ controlGroupRecall: { slot } });
  }

  /** Give one own hull a new name part (monetization.md §3.2, R1250). The
   * server keeps the designator prefix, checks the shape and the season's
   * used names, and answers a refusal with an error notice. */
  sendRenameFleet(fleetId: number, name: string) {
    this.send({ renameFleet: { fleetId, name } });
  }

  /** Name one seamount the commander's hull found (v2-the-deep.md §4.3,
   * R2620, R2626): a free text, or the two places of the word list. The
   * server checks the finder, the key and the name, and answers a refusal
   * with an error notice. */
  sendNameSeamount(n: NameSeamount) {
    this.send({ nameSeamount: n });
  }

  /** REPORT one piece of content another player wrote (deployment.md
   * §7.6, R1323). The intent names the piece by what it is — a line id,
   * an account id, a fleet id, a seamount id (R2620) or a cache id
   * (R2649) — and never a copy of the words. The server sends no answer. */
  sendReport(kind: ReportKind, piece: { lineId?: number; accountId?: number; fleetId?: number; body?: number; cache?: number },
    reason: string, note: string) {
    this.send({ report: { kind, ...piece, reason, note } });
  }

  /** BLOCK one commander of this world, by the world account id
   * (deployment.md §7.6, R1328). The server sends no answer. */
  sendBlock(accountId: number) {
    this.send({ block: { accountId } });
  }

  sendExpedition(depth: number, captainId: number) {
    this.send({ expedition: { depth, captainId } });
  }

  sendDesignAction(a: {
    design?: number; scrap?: boolean; useDataCore?: boolean;
    printRuns?: number; count?: number; install?: boolean;
    redraft?: boolean; redraftSlot?: number;
  }) {
    this.send({ design: a });
  }

  /** Enter one category's queue, or every queue at once (arcade.md §5.1).
   * `partyId` rides from day one and does nothing yet. */
  sendArcadeQueueJoin(category: string, quickjoin = false, partyId = '') {
    this.send({ arcadeQueueJoin: { category, quickjoin, partyId } });
  }

  /** Leave every queue this connection stands in. A LEAVE from a running
   *  drill ends the drill at once (arcade.md §17.1). */
  sendArcadeQueueLeave() {
    this.send({ arcadeQueueLeave: {} });
  }

  /** Start a drill at once, with no queue (arcade.md §17.5, R1564). Two
   *  empty fields start the first drill of the server. A refusal comes
   *  back as an ArcadeQueue frame in state REFUSED. */
  sendArcadeDrill(category = '', mapId = '') {
    this.send({ arcadeDrill: { category, mapId } });
  }

  /** NEXT on a reading drill step (done check `ack`). `step` is the index
   *  of the step the card shows; the hub ignores a press for any other
   *  step (arcade.md §17.3). */
  sendArcadeDrillNext(step: number) {
    this.send({ arcadeDrillNext: { step } });
  }

  /** SURRENDER of the readout line: the seat leaves the running match and
   *  its team plays on (arcade.md §6.2, R2000, R2013). The hub refuses it
   *  outside FIGHT and in a drill; the verdict comes back as the phase and
   *  the end card. */
  sendArcadeSurrender() {
    this.send({ arcadeSurrender: {} });
  }

  /** The instant build (arcade.md §6): a ship variant by the name the map
   * gave it, a round by kind, or a sea object by kind into the seat stock
   * (R3220). The server refuses with a notice, and the truth arrives in the
   * next Empire frame. */
  sendArcadeBuild(a: { variantName?: string; torpedoKind?: TorpedoKind; seaObjectKind?: SeaObjectKind }) {
    this.send({ arcadeBuild: a });
  }

  /**
   * One second of this seat's pointer and keyboard work (R907).
   *
   * The batch travels only while the match's `record_input` is on, the
   * hub writes it into the replay file alone, and no rule of the sim
   * reads a field of it. `fast/arcade/telemetry.ts` builds it.
   */
  sendArcadeInput(batch: DeepPartial<ArcadeInput>) {
    this.send({ arcadeInput: batch });
  }

  /** Opens a hosting for a private game (arcade.md §16.1). The server
   * mints the code and answers with the first `ArcadeHosting` push. */
  sendArcadeHost() {
    this.send({ arcadeHost: {} });
  }

  /** Joins a hosting by its three symbols. The server normalises the
   * code and answers every refusal as an `ArcadeHosting` reason. */
  sendArcadeHostJoin(code: string) {
    this.send({ arcadeHostJoin: { code } });
  }

  /** One act inside the hosting window (arcade.md §16.3). The server owns
   * every rule: it refuses a game-host act from an observer and answers
   * in an `ArcadeHosting` push. */
  sendArcadeHostAction(a: {
    kind: ArcadeHostActionKind; slot?: number; accountId?: number;
    category?: string; mapId?: string; delaySec?: number;
  }) {
    this.send({ arcadeHostAction: a });
  }

  /**
   * One casino intent (casino.md §5). Six wire messages, one method.
   *
   * The casino has six intents and every one of them is a press in the
   * same panel, so they travel through one discriminated union rather
   * than six methods: a panel hands `sendCasino` an object, the `kind`
   * names the arm of the `ClientMsg` oneof, and nothing else about the
   * call site changes when a new field joins a message. The union is
   * `CasinoIntent` above this method, and every optional field defaults
   * here, so a caller states what it means and nothing more.
   *
   * The client rules on nothing. A refused act comes back as a notice and
   * the truth comes back in the next `CasinoTable` frame.
   */
  sendCasino(a: CasinoIntent) {
    switch (a.kind) {
      case 'queue-join':
        this.send({ casinoQueueJoin: { game: a.game, stakeTier: a.stakeTier } });
        break;
      case 'queue-leave':
        this.send({ casinoQueueLeave: {} });
        break;
      case 'join-table':
        this.send({ casinoJoinTable: { tableId: a.tableId } });
        break;
      case 'leave':
        this.send({ casinoLeave: {} });
        break;
      case 'act':
        this.send({ casinoAct: {
          action: a.action, amount: a.amount, betKind: a.betKind, betNumbers: a.betNumbers,
        } });
        break;
      case 'spin':
        this.send({ casinoSpin: { stakeTier: a.stakeTier } });
        break;
    }
  }

  takeStats(): { bytesIn: number; msgsIn: number } {
    const s = { bytesIn: this.bytesIn, msgsIn: this.msgsIn };
    this.bytesIn = 0;
    this.msgsIn = 0;
    return s;
  }
}
